import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import HocPopup from "../../../../hoc/hocPopup";
import { useTheme } from "@mui/material/styles";
import { ManagerStyle } from "./Manager.style";
import { capitalizeFirstLetter } from "../../../../utils/Utils";
import { useAppContext } from "../../../../context/App.context";
import moment from "moment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useAuth } from "../../../../context/Auth.context";
import EventIcon from "@mui/icons-material/Event";
import { CustomerStyle } from "../../insurance/Customers.styles";

/* Default date range: first of the current month through today */
const getDefaultDateRange = () => ({
  startDate: moment().startOf("month").format("MM/DD/YYYY"),
  endDate: moment().format("MM/DD/YYYY"),
  d_startDate: moment().startOf("month").format("YYYY-MM-DD"),
  d_endDate: moment().format("YYYY-MM-DD"),
});

// Escape a value for CSV: wrap in quotes if it contains a comma, quote or newline
const escapeCsv = (value) => {
  const str = String(value ?? "");
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

// Shared inline "(+n)" badge, same visual language as the KPI cards
// on Home.component.jsx (small, bold, primary-green). Renders nothing extra
// in layout terms — just sits right after the base number in a table cell.
const FollowUpBadge = ({ value, color }) => (
  <Box
    component="span"
    sx={{
      color: color,
      fontSize: "0.85em",
      fontWeight: 600,
      ml: 0.5,
    }}
  >
    (+{value ?? 0})
  </Box>
);

const ManagerComponent = ({
  filterData,
  fetchInput,
  setFetchInput,
  setFilterData,
  checkService,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const app = useAppContext();
  const auth = useAuth();

  // MUI TablePagination is 0-indexed
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchText, setSearchText] = useState("");

  const handleReset = () => {
    const defaults = getDefaultDateRange();
    setFilterData({
      ...filterData,
      startDate: defaults.startDate,
      endDate: defaults.endDate,
    });
    setFetchInput({
      ...fetchInput,
      d_startDate: defaults.d_startDate,
      d_endDate: defaults.d_endDate,
    });
    setSearchText("");
    setPage(0);
  };

  const filteredTarget = app.target
    ? app.target.filter((row) => {
        if (!searchText.trim()) return true;
        const fullName = `${row.first_name || ""} ${row.last_name || ""}`
          .trim()
          .toLowerCase();
        return fullName.includes(searchText.trim().toLowerCase());
      })
    : [];

  const totalRecords = filteredTarget.length;
  const startIdx = page * rowsPerPage;
  const pagedRows = filteredTarget.slice(startIdx, startIdx + rowsPerPage);

  // CSV export: all filtered rows (not just the current page). Each "(+n)"
  // follow-up count gets its own numeric column so it can be summed/sorted.
  const handleExport = () => {
    const headers = [
      "Employee",
      "Insurance Verified",
      "Insurance Verified Follow-up",
      ...(checkService?.knowledgeHub ? ["Knowledge Hub Run"] : []),
      ...(checkService?.idVerfication ? ["ID Verification"] : []),
      ...(checkService?.prequal ? ["Prequals Run"] : []),
      "Quotes Requested",
      "Reviews Provided",
      "Reviews Follow-up",
      "Satisfied Yes",
      "Satisfied Yes Follow-up",
      "Satisfied No",
      "Satisfied No Follow-up",
      "1st Service Set",
      "1st Service Set Follow-up",
      "Referrals Provided",
      "Referrals Follow-up",
      "Apps Downloaded",
    ];

    const rows = filteredTarget.map((row) => [
      `${capitalizeFirstLetter(row.first_name) || ""} ${
        capitalizeFirstLetter(row.last_name) || ""
      }`.trim(),
      row.completed || 0,
      row.verified_followup || 0,
      ...(checkService?.knowledgeHub ? [row.watched_artifacts || 0] : []),
      ...(checkService?.idVerfication ? ["N/A"] : []),
      ...(checkService?.prequal ? ["N/A"] : []),
      row.quotes_requested || 0,
      row.review_provided || 0,
      row.review_followup || 0,
      row.satisfied_yes || 0,
      row.satisfied_yes_followup || 0,
      row.satisfied_no || 0,
      row.satisfied_no_followup || 0,
      row.appointment_set || 0,
      row.appointment_followup || 0,
      row.referral_provided || 0,
      row.referral_followup || 0,
      row.apps_downloaded || 0,
    ]);

    const csv = [
      [`${capitalizeFirstLetter(auth.user.dealership_name)} Employee Report`],
      [`Date Range: ${filterData?.startDate} - ${filterData?.endDate}`],
      [],
      headers,
      ...rows,
    ]
      .map((line) => line.map(escapeCsv).join(","))
      .join("\r\n");

    // BOM so Excel reads UTF-8 correctly
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Employee_Report_${moment().format("YYYY-MM-DD")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Paper
      sx={{
        ...ManagerStyle.wrapper,
        display: "flex",
        flexDirection: "column",
        height: "440px",
      }}
    >
      {/* Header & Filter Controls Section (Height ~36px buttons, 16px vertical gap) */}
      <Box
        sx={{
          display: { xs: "block", md: "flex" },
          padding: "12px 16px 8px 16px",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "16px" }}>
          {capitalizeFirstLetter(auth.user.dealership_name)} Employees
        </Typography>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "8px",
            marginTop: "4px",
          }}
        >
          <TextField
            size="small"
            placeholder="Search employee"
            value={searchText}
            onChange={(event) => {
              setSearchText(event.target.value);
              setPage(0);
            }}
            sx={{
              ...CustomerStyle.textField,
              width: "180px",

              "& .MuiInputBase-root": { height: "36px", fontSize: "13px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#919EAB" }} />
                </InputAdornment>
              ),
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText("");
                      setPage(0);
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={filterData.startDate}
              maxDate={new Date()}
              onChange={(event) => {
                setFilterData({
                  ...filterData,
                  startDate: moment(event).format("MM/DD/YYYY"),
                });
                setFetchInput({
                  ...fetchInput,
                  d_startDate: moment(event).format("YYYY-MM-DD"),
                });
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField
                  size="small"
                  sx={{
                    ...CustomerStyle.textField,
                    "& .MuiInputBase-root": { height: "36px", fontSize: "13px" },
                  }}
                  {...params}
                />
              )}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton edge="end" size="small">
                    <EventIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              }
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="End Date"
              value={filterData.endDate}
              minDate={new Date(filterData.startDate)}
              maxDate={new Date()}
              onChange={(event) => {
                setFilterData({
                  ...filterData,
                  endDate: moment(event).format("MM/DD/YYYY"),
                });
                setFetchInput({
                  ...fetchInput,
                  d_endDate: moment(event).format("YYYY-MM-DD"),
                });
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField
                  size="small"
                  sx={{
                    ...CustomerStyle.textField,
                    "& .MuiInputBase-root": { height: "36px", fontSize: "13px" },
                  }}
                  {...params}
                />
              )}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton edge="end" size="small">
                    <EventIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              }
            />
          </LocalizationProvider>

          <Button
            onClick={handleReset}
            startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
            variant="outlined"
            size="small"
            sx={{
              height: "36px",
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 700,
              borderColor: "#BDBDBD",
              color: "#616161",
              whiteSpace: "nowrap",
              px: "12px",
            }}
          >
            Reset
          </Button>

          <Button
            onClick={handleExport}
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            variant="outlined"
            size="small"
            sx={{
              height: "36px",
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 700,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              whiteSpace: "nowrap",
              px: "12px",
              "&:hover": {
                backgroundColor: "rgba(0,171,85,0.08)",
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            Export
          </Button>
        </div>
      </Box>

      {/* Table Section */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: "12px",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <Table stickyHeader size="small">
          <TableHead sx={ManagerStyle.tableHead}>
            <TableRow sx={ManagerStyle.tableRow}>
              <TableCell sx={ManagerStyle.tableCell} width={"14%"}>
                Employee
              </TableCell>
              <TableCell sx={ManagerStyle.tableCell}>Completed</TableCell>
              {checkService?.knowledgeHub && (
                <TableCell sx={ManagerStyle.tableCell}>
                  Knowledge Hub
                </TableCell>
              )}
              {checkService?.idVerfication && (
                <TableCell sx={ManagerStyle.tableCell}>
                  ID Verification (P/F)
                </TableCell>
              )}
              {checkService?.prequal && (
                <TableCell sx={ManagerStyle.tableCell}>PreQuals Run</TableCell>
              )}
              <TableCell sx={ManagerStyle.tableCell}>
                Quotes Requested
              </TableCell>
              <TableCell sx={ManagerStyle.tableCell}>Review</TableCell>
              <TableCell sx={ManagerStyle.tableCell}>Satisfied (Y/N)</TableCell>
              <TableCell sx={ManagerStyle.tableCell}>Service Appt</TableCell>
              <TableCell sx={ManagerStyle.tableCell}>Referrals</TableCell>
              <TableCell sx={ManagerStyle.tableCell}>App Download</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!app.target ? (
              <TableRow>
                <TableCell sx={ManagerStyle.noBorder} colSpan={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : pagedRows.length === 0 ? (
              <TableRow>
                <TableCell sx={ManagerStyle.noBorder} colSpan={12}>
                  <Typography
                    align="center"
                    sx={{ fontSize: "13px", color: "#637381", py: 2 }}
                  >
                    No employees found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pagedRows.map((row) => (
                <TableRow
                  key={row.employee_user_id}
                  onClick={() => {
                    navigate(`/customers/${row.employee_user_id}/`);
                  }}
                  sx={{
                    height: "28px",
                    "&:hover": {
                      backgroundColor: "rgba(0,171,85,0.05)",
                      cursor: "pointer",
                      "& > *": {
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      ...ManagerStyle.noBorder,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      maxWidth: "20ch",
                      py: "4px",
                    }}
                  >
                    {row?.role === "manager" ? (
                      <GroupIcon
                        sx={{
                          color: theme.palette.primary.main,
                          width: "18px",
                          height: "18px",
                        }}
                      />
                    ) : (
                      <GroupIcon
                        sx={{
                          visibility: "hidden",
                          width: "18px",
                          height: "18px",
                        }}
                      />
                    )}
                    <Tooltip
                      title={`${row.first_name} ${row.last_name}`}
                      placement="top"
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "inline-block",
                          maxWidth: "100%",
                          fontSize: "13px",
                        }}
                      >
                        {capitalizeFirstLetter(row.first_name) +
                          " " +
                          capitalizeFirstLetter(row.last_name)}
                      </span>
                    </Tooltip>
                  </TableCell>

                  {/* Completed → Insurance Verified "(+value)" = verification_repull reruns */}
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {row.completed || 0}
                    <FollowUpBadge
                      value={row?.verified_followup}
                      color={theme.palette.primary.main}
                    />
                  </TableCell>
                  {checkService?.knowledgeHub && (
                    <TableCell align="center" sx={ManagerStyle.noBorder}>
                      {row?.watched_artifacts || 0}
                    </TableCell>
                  )}
                  {checkService?.idVerfication && (
                    <TableCell align="center" sx={ManagerStyle.noBorder}>
                      {0}
                      {"/"}
                      <Box component="span" sx={{ color: theme.palette.error.main }}>
                        {0}
                      </Box>
                    </TableCell>
                  )}
                  {checkService?.prequal && (
                    <TableCell align="center" sx={ManagerStyle.noBorder}>
                      {0}
                    </TableCell>
                  )}
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {row.quotes_requested || 0}
                  </TableCell>
                  {/* Review → Reviews Provided = isReview rows + is_followup_review rows.
                      "(+value)" = is_followup_review only (already included in the total). */}
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {row?.review_provided || 0}
                    <FollowUpBadge
                      value={row?.review_followup}
                      color={theme.palette.primary.main}
                    />
                  </TableCell>
                  {/* Satisfied (Y/N) → Direct Yes/No | Follow-up Yes/No.
                      The "No" value in both the direct and follow-up pair is
                      shown in the theme's error (red) color. */}
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {row?.satisfied_yes || 0}
                    {"/"}
                    <Box component="span" sx={{ color: theme.palette.error.main }}>
                      {row?.satisfied_no || 0}
                    </Box>
                    {" | "}
                    {row?.satisfied_yes_followup || 0}
                    {"/"}
                    <Box component="span" sx={{ color: theme.palette.error.main }}>
                      {row?.satisfied_no_followup || 0}
                    </Box>
                  </TableCell>
                  {/* Service Appt → 1st Service Set "(+value)" = follow-up scheduling link (service_maintenance_vehicle) */}
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {row?.appointment_set || 0}
                    <FollowUpBadge
                      value={row?.appointment_followup}
                      color={theme.palette.primary.main}
                    />
                  </TableCell>
                  {/* Referrals → Referrals Provided "(+value)" = follow-up referral link (is_followup) */}
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {row.referral_provided || 0}
                    <FollowUpBadge
                      value={row?.referral_followup}
                      color={theme.palette.primary.main}
                    />
                  </TableCell>
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {row?.apps_downloaded || 0}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Table Footer / Pagination Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "0 16px",
          borderTop: "1px solid #F4F6F8",
          mt: "auto",
          pt: "4px",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => {
            setPage(newPage);
          }}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Box>
    </Paper>
  );
};

export default HocPopup(ManagerComponent);